/**
 * Design Day Injector Service
 *
 * Handles injection of design days from DDY files into IDF models.
 * Manages conflict detection and resolution.
 */

import { parseIDF } from '../../parser/parser';
import { useIDFStore } from '../../stores/idfStore';
import type { ParsedDesignDay, DesignDayInjectionResult, DesignDayInjectionOptions } from '../types';

/**
 * Check for existing design days with conflicting names.
 *
 * @param designDays - Design days to check
 * @returns Array of conflicting design day names
 */
export function checkDesignDayConflicts(designDays: ParsedDesignDay[]): string[] {
  const document = useIDFStore.getState().document;
  if (!document) return [];

  const conflicts: string[] = [];

  const existingNames = new Set(
    document.objects
      .filter((obj) => obj.className.toLowerCase() === 'sizingperiod:designday')
      .map((obj) => obj.fields[0]?.value?.toLowerCase())
      .filter((name): name is string => !!name)
  );

  for (const dd of designDays) {
    const ddName = dd.name.toLowerCase();
    if (existingNames.has(ddName)) {
      conflicts.push(dd.name);
    }
  }

  return conflicts;
}

/**
 * Inject selected design days into the current IDF model.
 *
 * @param designDays - Design days to inject
 * @param options - Injection options (conflict handling)
 * @returns Result with counts of added, skipped, replaced
 */
export function injectDesignDays(
  designDays: ParsedDesignDay[],
  options: DesignDayInjectionOptions = { onConflict: 'skip' }
): DesignDayInjectionResult {
  const result: DesignDayInjectionResult = {
    added: 0,
    skipped: 0,
    replaced: 0,
    errors: [],
  };

  const store = useIDFStore.getState();
  const { document, addObject, removeObject } = store;

  // Build map of existing design day names to object IDs
  const existingDesignDays = new Map<string, string>();
  if (document) {
    for (const obj of document.objects) {
      if (obj.className.toLowerCase() === 'sizingperiod:designday') {
        const name = obj.fields[0]?.value?.toLowerCase();
        if (name) {
          existingDesignDays.set(name, obj.id);
        }
      }
    }
  }

  for (const dd of designDays) {
    try {
      const ddNameLower = dd.name.toLowerCase();
      const existingId = existingDesignDays.get(ddNameLower);

      if (existingId) {
        // Handle conflict
        switch (options.onConflict) {
          case 'skip':
            result.skipped++;
            continue;

          case 'replace':
            // Remove existing object
            removeObject(existingId);
            result.replaced++;
            break;

          case 'rename':
            // Will be handled below by modifying the name
            break;
        }
      }

      // Parse the IDF object from the stored text
      const parseResult = parseIDF(dd.idfObject);
      if (!parseResult.success || parseResult.document.objects.length === 0) {
        result.errors.push(`Failed to parse design day: ${dd.name}`);
        continue;
      }

      const idfObj = parseResult.document.objects[0];
      if (!idfObj) {
        result.errors.push(`No object found in design day: ${dd.name}`);
        continue;
      }

      // Handle rename conflict option
      if (options.onConflict === 'rename' && existingId && idfObj.fields[0]) {
        // Add suffix to make unique
        let suffix = 1;
        let newName = `${dd.name} (imported)`;
        while (existingDesignDays.has(newName.toLowerCase())) {
          suffix++;
          newName = `${dd.name} (imported ${suffix})`;
        }
        idfObj.fields[0].value = newName;
      }

      // Add the object to the document
      const { id: _id, ...objWithoutId } = idfObj;
      addObject(objWithoutId);
      result.added++;

      // Update our tracking map to catch duplicates within the same batch
      if (idfObj.fields[0]?.value) {
        existingDesignDays.set(idfObj.fields[0].value.toLowerCase(), 'added');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      result.errors.push(`Error injecting ${dd.name}: ${message}`);
    }
  }

  return result;
}

/**
 * Get existing design days from the current IDF model.
 *
 * @returns Array of design day names in the model
 */
export function getExistingDesignDays(): string[] {
  const document = useIDFStore.getState().document;
  if (!document) return [];

  return document.objects
    .filter((obj) => obj.className.toLowerCase() === 'sizingperiod:designday')
    .map((obj) => obj.fields[0]?.value || 'Unnamed')
    .filter((name): name is string => !!name);
}

/**
 * Remove all design days from the current IDF model.
 *
 * @returns Number of design days removed
 */
export function removeAllDesignDays(): number {
  const store = useIDFStore.getState();
  const { document, removeObject } = store;

  if (!document) return 0;

  const designDayIds = document.objects
    .filter((obj) => obj.className.toLowerCase() === 'sizingperiod:designday')
    .map((obj) => obj.id);

  for (const id of designDayIds) {
    removeObject(id);
  }

  return designDayIds.length;
}
